import React from 'react'
import { Header, Form, Dropdown, Button, Placeholder, Icon } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import SkeletonTable from '../../../../../../common-components/SkeletonTable/SkeletonTable'

const READ_ONLY_DETAIL_SKELETONS = [
  {
    id: 'bk_dv_bookingReferenceId',
    icon: 'book',
    label: 'Booking Reference #',
  },
  { id: 'bk_dv_createdBy', icon: 'user', labelKey: 'Created By' },
  { id: 'bk_dv_venueAddress', icon: 'marker', label: 'Venue Address' },
  { id: 'bk_dv_venueOpenTimes', icon: 'clock', label: 'Venue Open Times' },
  { id: 'bk_dv_dateRequested', icon: 'calendar', labelKey: 'Date Requested' },
  {
    id: 'bk_dv_mostRecentCollectionDate',
    icon: 'dolly',
    label: 'Most Recent Collection Date',
  },
  {
    id: 'bk_dv_preferredCollector',
    icon: 'truck',
    label: 'Preferred Collector',
  },
  {
    id: 'bk_dv_contactPhoneNumber',
    icon: 'phone',
    labelKey: 'contactPhoneNumber',
  },
]

const TextareaSkeleton = () => (
  <div className="bk_dv_bookingDetailsTextarea" style={{ minHeight: 100 }}>
    <Placeholder fluid>
      <Placeholder.Paragraph>
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
      </Placeholder.Paragraph>
    </Placeholder>
  </div>
)

const DropdownSkeleton = ({ id, placeholder }) => (
  <Dropdown
    id={id}
    selection
    fluid
    placeholder={placeholder}
    loading
    disabled
    options={[]}
    value={null}
    onChange={() => {}}
  />
)

const DateFieldSkeleton = ({ id, label }) => (
  <Form>
    <Form.Field>
      <label htmlFor={id} className="bk_dv_formFieldLabel">
        {label}
      </label>
      <DropdownSkeleton id={id} placeholder="Loading…" />
    </Form.Field>
  </Form>
)

const DetailedViewSkeletonLoader = ({ tableRows = 5, tableColumns = 7 }) => {
  const { t: translate } = useTranslation()

  return (
    <>
      {/* Page heading */}
      <Header as="h2" color="grey">
        <Placeholder>
          <Placeholder.Header>
            <Placeholder.Line length="very long" />
            <Placeholder.Line length="long" />
          </Placeholder.Header>
        </Placeholder>
      </Header>

      {/* Read-only booking details segment */}
      <div className="bk_dv_parentSegment">
        <Header as="h3" color="grey" content="Booking Details" />
        <div className="bk_dv_readOnlyDetails">
          {READ_ONLY_DETAIL_SKELETONS.map(detail => (
            <div className="bk_dv_readOnlyDetailsSegment" key={detail.id}>
              <label>
                <Icon name={detail.icon} className="bk_dv_iconColor" />{' '}
                <span className="bk_dv_readOnlyDetailsLabel bk_primaryTextColor">
                  {detail.labelKey ? translate(detail.labelKey) : detail.label}
                </span>
              </label>
              <div className="bk_dv_readOnlyDetailsValue bk_secondaryTextColor">
                <Placeholder>
                  <Placeholder.Line length="medium" />
                </Placeholder>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Booking management segment */}
      <div className="bk_dv_parentSegment">
        <Header as="h3" color="grey" content="Booking Management" />

        <div className="bk_dv_bookingManagementDetailsTopRow">
          <Form>
            <Form.Field>
              <label htmlFor="bk_dv_bookingNotes" className="bk_primaryTextColor">
                {translate('bookingNotes')}
              </label>
              <TextareaSkeleton />
            </Form.Field>
          </Form>

          <Form>
            <Form.Field>
              <label htmlFor="bk_dv_venueNotes" className="bk_primaryTextColor">
                {translate('venueNotes')}
              </label>
              <TextareaSkeleton />
            </Form.Field>
          </Form>

          <Form>
            <Form.Field>
              <label htmlFor="collectorsDropdown" className="bk_primaryTextColor">
                {translate('Collector')}
              </label>
              <DropdownSkeleton id="collectorsDropdown" placeholder="Loading collectors…" />
            </Form.Field>
          </Form>

          <Form>
            <Form.Field>
              <label htmlFor="bk_dv_statusDropdown" className="bk_primaryTextColor">
                {translate('Status')}
              </label>
              <DropdownSkeleton id="bk_dv_statusDropdown" placeholder="Loading…" />
            </Form.Field>
          </Form>
        </div>

        <div className="bk_dv_bookingManagementDetailsBottomRow">
          <DateFieldSkeleton
            id="bk_dv_startEstimatedPickupDate"
            label={translate('startEstimatedPickupDate')}
          />
          <DateFieldSkeleton
            id="bk_dv_endEstimatedPickupDate"
            label={translate('endEstimatedPickupDate')}
          />
          <DateFieldSkeleton id="bk_dv_actualPickupDate" label={translate('actualPickupDate')} />
        </div>

        <div className="bk_dv_bookingsManagementSaveBtnRow">
          <div className="bk_dv_showChangesUnsavedHelperText">
            <Placeholder fluid>
              <Placeholder.Line length="long" />
            </Placeholder>
          </div>
          <Button color="green" className="bk_dv_saveButtonFix" disabled loading>
            <Icon name="save" />
            {translate('saveChangesBtnLabel')}
          </Button>
        </div>
      </div>

      {/* Keg collection requests segment */}
      <div className="bk_dv_parentSegment">
        <Header as="h3" color="grey" content={translate('kegCollectionRequests')} />
        <SkeletonTable rows={tableRows} columns={tableColumns} />
      </div>
    </>
  )
}

export default React.memo(DetailedViewSkeletonLoader)
